/**
 * NACLessons.jsx
 * Snows ProAm — Sport Ireland Campus · Private NAC Swim Lessons
 * Matches PartnerPortal design system exactly
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";

/* ─────────────────────────────────────────
   THEME — identical to PartnerPortal
───────────────────────────────────────── */
const DARK = {
  mode:          "dark",
  accent:        "#0C69C8",
  accentDark:    "#0A1D44",
  accentLight:   "#5BA8F7",
  accentGrad:    "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:    "rgba(12,105,200,0.35)",
  accentBg:      "rgba(12,105,200,0.10)",
  accentBgLight: "rgba(12,105,200,0.14)",
  accentBorder:  "rgba(12,105,200,0.28)",
  bg:            "#0A0A0A",
  bgCard:        "#141414",
  bgElevated:    "#1E1E1E",
  bgHover:       "#242424",
  border:        "rgba(255,255,255,0.07)",
  borderMd:      "rgba(255,255,255,0.12)",
  text:          "#FAFAFA",
  textSec:       "rgba(250,250,250,0.65)",
  textMuted:     "rgba(250,250,250,0.36)",
  shadow:        "0 4px 20px rgba(0,0,0,0.55)",
  shadowLg:      "0 16px 56px rgba(0,0,0,0.75)",
  headerBg:      "rgba(10,10,10,0.93)",
  success:       "#34D399",
  successBg:     "rgba(52,211,153,0.10)",
  danger:        "#EF4444",
  dangerBg:      "rgba(239,68,68,0.10)",
  dangerBorder:  "rgba(239,68,68,0.24)",
  inputBg:       "rgba(255,255,255,0.04)",
  inputBorder:   "rgba(255,255,255,0.10)",
  gold:          "#F5C542",
};

const LIGHT = {
  mode:          "light",
  accent:        "#0C69C8",
  accentDark:    "#0A1D44",
  accentLight:   "#0C69C8",
  accentGrad:    "linear-gradient(135deg, #0C69C8 0%, #0A1D44 100%)",
  accentGlow:    "rgba(12,105,200,0.22)",
  accentBg:      "rgba(12,105,200,0.06)",
  accentBgLight: "rgba(12,105,200,0.09)",
  accentBorder:  "rgba(12,105,200,0.20)",
  bg:            "#F5F7FA",
  bgCard:        "#FFFFFF",
  bgElevated:    "#EDF0F5",
  bgHover:       "#E4E8F0",
  border:        "rgba(10,29,68,0.08)",
  borderMd:      "rgba(10,29,68,0.13)",
  text:          "#0F1629",
  textSec:       "#3D4A6B",
  textMuted:     "#7A87A8",
  shadow:        "0 4px 20px rgba(10,29,68,0.07)",
  shadowLg:      "0 16px 56px rgba(10,29,68,0.12)",
  headerBg:      "rgba(245,247,250,0.94)",
  success:       "#059669",
  successBg:     "rgba(5,150,105,0.08)",
  danger:        "#DC2626",
  dangerBg:      "rgba(220,38,38,0.07)",
  dangerBorder:  "rgba(220,38,38,0.20)",
  inputBg:       "#F8F9FC",
  inputBorder:   "rgba(10,29,68,0.12)",
  gold:          "#C9960A",
};

function useTheme() {
  const [dark, setDark] = useState(() => {
    try {
      const s = localStorage.getItem("spa-theme");
      if (s) return s !== "light";
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch { return true; }
  });
  const toggle = useCallback(() => {
    setDark(d => {
      const next = !d;
      try { localStorage.setItem("spa-theme", next ? "dark" : "light"); } catch {}
      return next;
    });
  }, []);
  return [dark ? DARK : LIGHT, toggle, dark];
}

/* ─────────────────────────────────────────
   GLOBAL CSS — same as PartnerPortal
───────────────────────────────────────── */
function injectCSS(C) {
  const id = "nac-lessons-css";
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("style"); el.id = id; document.head.appendChild(el); }
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800;900&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    html { scroll-behavior:smooth; }
    body { font-family:'DM Sans',sans-serif; background:${C.bg}; color:${C.text}; -webkit-font-smoothing:antialiased; overflow-x:hidden; transition:background .3s,color .3s; }
    ::-webkit-scrollbar { width:4px; }
    ::-webkit-scrollbar-track { background:transparent; }
    ::-webkit-scrollbar-thumb { background:${C.accentBorder}; border-radius:99px; }

    @keyframes fadeUp   { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
    @keyframes spin     { to{transform:rotate(360deg)} }
    @keyframes slideUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    @keyframes shimmer  { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
    @keyframes wave     { 0%,100%{transform:scaleX(1)} 50%{transform:scaleX(1.03)} }

    .fu  { animation:fadeUp .55s cubic-bezier(.16,1,.3,1) both; }
    .fu1 { animation:fadeUp .55s .06s cubic-bezier(.16,1,.3,1) both; }
    .fu2 { animation:fadeUp .55s .12s cubic-bezier(.16,1,.3,1) both; }
    .fu3 { animation:fadeUp .55s .18s cubic-bezier(.16,1,.3,1) both; }
    .fu4 { animation:fadeUp .55s .26s cubic-bezier(.16,1,.3,1) both; }

    .card-lift { transition:transform .2s ease,box-shadow .2s ease,border-color .2s ease; }
    .card-lift:hover { transform:translateY(-3px); box-shadow:${C.shadowLg}; }

    /* BUTTONS */
    .btn-primary {
      display:inline-flex; align-items:center; gap:8px;
      background:${C.accentGrad}; color:#fff; border:none; border-radius:10px;
      padding:11px 20px; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:700; cursor:pointer;
      transition:filter .15s,transform .15s,box-shadow .15s;
      box-shadow:0 4px 16px ${C.accentGlow};
      text-decoration:none;
    }
    .btn-primary:hover { filter:brightness(1.1); transform:translateY(-1px); box-shadow:0 8px 24px ${C.accentGlow}; }
    .btn-primary:active { transform:scale(.97); }

    .btn-ghost {
      display:inline-flex; align-items:center; gap:7px;
      background:transparent; color:${C.textSec};
      border:1px solid ${C.border}; border-radius:10px;
      padding:10px 18px; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:600; cursor:pointer;
      transition:all .15s; text-decoration:none;
    }
    .btn-ghost:hover { border-color:${C.borderMd}; color:${C.text}; background:${C.bgHover}; }

    /* TABS */
    .tab-pill {
      display:inline-flex; align-items:center; gap:6px;
      padding:8px 16px; border-radius:9px; font-size:13px;
      font-weight:600; cursor:pointer; border:1px solid transparent;
      transition:all .15s; background:transparent; color:${C.textMuted};
      white-space:nowrap; font-family:'DM Sans',sans-serif;
    }
    .tab-pill.active { background:${C.accentBg}; color:${C.accent}; border-color:${C.accentBorder}; }
    .tab-pill:not(.active):hover { background:${C.bgHover}; color:${C.textSec}; }

    /* BADGES */
    .badge { display:inline-flex; align-items:center; gap:5px; padding:3px 9px; border-radius:999px; font-size:10px; font-weight:700; letter-spacing:.3px; }
    .badge-accent  { background:${C.accentBg};   color:${C.accent};   border:1px solid ${C.accentBorder}; }
    .badge-success { background:${C.successBg};  color:${C.success};  border:1px solid ${C.success}33; }

    /* MODAL */
    .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.72); backdrop-filter:blur(8px); z-index:1000; display:flex; align-items:flex-end; justify-content:center; animation:fadeIn .2s ease; }
    @media(min-width:640px){ .modal-overlay{ align-items:center; } }
    .modal-sheet { background:${C.bgCard}; border:1px solid ${C.borderMd}; border-radius:20px 20px 0 0; width:100%; max-width:580px; max-height:92vh; overflow-y:auto; animation:slideUp .28s cubic-bezier(.16,1,.3,1); }
    @media(min-width:640px){ .modal-sheet{ border-radius:20px; } }

    /* HEADINGS */
    .display-head { font-family:'Sora',sans-serif; font-size:clamp(24px,4vw,42px); font-weight:900; color:${C.text}; letter-spacing:-.5px; line-height:1.05; }
    .section-head { font-family:'Sora',sans-serif; font-size:clamp(18px,3vw,24px); font-weight:800; color:${C.text}; letter-spacing:-.2px; }
    .section-sub  { font-size:13px; color:${C.textSec}; line-height:1.65; margin-top:4px; }

    /* ACCENT LINE */
    .accent-line { width:36px; height:3px; background:${C.accentGrad}; border-radius:2px; margin-bottom:12px; }

    /* THEME TOGGLE */
    .theme-btn { width:36px; height:36px; border-radius:9px; border:1px solid ${C.border}; background:${C.bgElevated}; color:${C.textSec}; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
    .theme-btn:hover { border-color:${C.borderMd}; color:${C.text}; }

    /* GRAD TEXT */
    .grad-text { background:${C.accentGrad}; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }

    /* SEARCH INPUT */
    .search-input {
      background:${C.inputBg}; border:1.5px solid ${C.inputBorder};
      border-radius:10px; color:${C.text}; font-family:'DM Sans',sans-serif;
      font-size:13px; font-weight:500; padding:10px 14px 10px 38px;
      outline:none; transition:border-color .15s,box-shadow .15s; width:100%;
    }
    .search-input:focus { border-color:${C.accent}; box-shadow:0 0 0 3px ${C.accentBg}; }
    .search-input::placeholder { color:${C.textMuted}; }

    /* COACH CARD */
    .coach-card { background:${C.bgCard}; border:1px solid ${C.border}; border-radius:18px; overflow:hidden; }
    .coach-card:hover { border-color:${C.accentBorder}; box-shadow:${C.shadow}; }
    .coach-img-wrap { position:relative; height:220px; overflow:hidden; background:${C.bgElevated}; }
    .coach-img { width:100%; height:100%; object-fit:cover; transition:transform .4s ease; }
    .coach-card:hover .coach-img { transform:scale(1.04); }

    /* GALLERY DEL */
    .gallery-del { position:absolute; top:6px; right:6px; width:26px; height:26px; border-radius:50%; background:rgba(0,0,0,.7); border:none; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .15s; z-index:3; }

    @media(max-width:640px) { .hide-mobile { display:none !important; } }

    /* Mobile responsive */
    @media(max-width:768px) {
      .search-input { width:160px; }
      .info-grid { grid-template-columns:1fr !important; }
      .coach-grid { grid-template-columns:1fr !important; }
      .header-btn { padding:6px 10px !important; font-size:11px !important; }
      .header-title { font-size:12px !important; }
    }
    @media(max-width:480px) {
      .search-input { width:130px; }
      .header-title { font-size:11px !important; }
      .hide-mobile-xs { display:none !important; }
    }
  `;
}

/* ─────────────────────────────────────────
   ICONS — same paths as PartnerPortal
───────────────────────────────────────── */
const PATHS = {
  sun:      "M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.79 1.42-1.41zM4 10.5H1v2h3v-2zm9-9.95h-2V3.5h2V.55zm7.45 3.91l-1.41-1.41-1.79 1.79 1.41 1.41 1.79-1.79zm-3.21 13.7l1.79 1.8 1.41-1.41-1.8-1.79-1.4 1.4zM20 10.5v2h3v-2h-3zm-8-5c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm-1 16.95h2V19.5h-2v2.95zm-7.45-3.91l1.41 1.41 1.79-1.8-1.41-1.41-1.79 1.8z",
  moon:     "M10 2c-1.82 0-3.53.5-5 1.35C7.99 5.08 10 8.3 10 12s-2.01 6.92-5 8.65C6.47 21.5 8.18 22 10 22c5.52 0 10-4.48 10-10S15.52 2 10 2z",
  close:    "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  check:    "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  search:   "M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  star:     "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
  info:     "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
  trophy:   "M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V18H8v2h8v-2h-3v-2.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2z",
  calendar: "M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z",
  location: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z",
  phone:    "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.19 2.2z",
  globe:    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z",
  mail:     "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  dive:     "M12 2C9.24 2 7 4.24 7 7c0 1.67.83 3.15 2.1 4.06L5 22h14l-4.1-10.94C16.17 10.15 17 8.67 17 7c0-2.76-2.24-5-5-5zm0 2c1.65 0 3 1.35 3 3s-1.35 3-3 3-3-1.35-3-3 1.35-3 3-3z",
  swim:     "M22 21c-1.11 0-1.73-.37-2.18-.64-.37-.22-.6-.36-1.15-.36-.54 0-.78.14-1.15.36-.46.27-1.07.64-2.18.64s-1.73-.37-2.18-.64c-.37-.22-.6-.36-1.15-.36-.54 0-.78.14-1.15.36-.46.27-1.08.64-2.19.64-1.11 0-1.73-.37-2.18-.64-.37-.22-.6-.36-1.15-.36s-.78.14-1.15.36C4.73 20.63 4.11 21 3 21v2c1.11 0 1.73-.37 2.18-.64.37-.22.6-.36 1.15-.36s.78.14 1.15.36c.46.27 1.08.64 2.19.64 1.11 0 1.73-.37 2.18-.64.37-.22.6-.36 1.15-.36.54 0 .78.14 1.15.36.46.27 1.07.64 2.18.64s1.73-.37 2.18-.64c.37-.22.6-.36 1.15-.36.54 0 .78.14 1.15.36.46.27 1.07.64 2.18.64v-2zM7 20c1.11 0 1.73-.37 2.18-.64.37-.22.6-.36 1.15-.36.54 0 .78.14 1.15.36.46.27 1.08.64 2.19.64v-2c-.54 0-.78-.14-1.15-.36C12.05 17.37 11.43 17 10.32 17s-1.73.37-2.18.64c-.37.22-.6.36-1.15.36V20zM16.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5.97 11.43c.37.22.6.36 1.15.36 1.11 0 1.73-.37 2.18-.64.37-.22.6-.36 1.15-.36.54 0 .78.14 1.15.36.44.26 1.04.61 2.07.64l2.29-2.29c-.42-.2-.79-.5-1.04-.92L13.36 6H18v2l2-2-2-2H13l-.94.94C11.48 5.34 10.76 5 9.98 5 8.85 5 8.27 5.27 7.39 5.7L5 8.11c-.6.57-.95 1.37-.95 2.22C4 11.28 4.87 12 5.97 11.43z",
};

const Icon = ({ name, size = 16, color = "currentColor", style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style}>
    <path d={PATHS[name] || PATHS.info} />
  </svg>
);


const AGE_FILTERS = ["All", "Children", "Adults", "Adaptive"];

/* ─────────────────────────────────────────
   COACH MODAL
───────────────────────────────────────── */
const CoachModal = ({ coach, onClose, C, ratingStats, reviews = [] }) => {
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);
  if (!coach) return null;

  const stats = ratingStats || { averageRating: 0, reviewCount: 0 };
  const displayRating = stats.averageRating > 0 ? stats.averageRating.toFixed(1) : "—";
  const reviewCount = stats.reviewCount || 0;

  const ageBg = { Children: C.accentBg, Adults: "rgba(52,211,153,0.10)", Adaptive: "rgba(245,197,66,0.12)" };
  const ageColor = { Children: C.accent, Adults: C.success, Adaptive: C.gold };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet">
        {/* Image */}
        <div style={{ position: "relative", height: 260, overflow: "hidden", background: C.bgElevated, borderRadius: "20px 20px 0 0" }}>
          <img
            src={coach.img} alt={coach.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; }}
          />
          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${C.mode === "dark" ? "rgba(10,10,10,0.75)" : "rgba(10,29,68,0.55)"} 0%, transparent 55%)` }} />
          <button aria-label="Close dialog" onClick={onClose} style={{ position: "absolute", top: 14, right: 14, width: 32, height: 32, borderRadius: "50%", background: "rgba(0,0,0,0.55)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
            <Icon name="close" size={16} color="#fff" />
          </button>
          {/* Rating pill over image */}
          <div style={{ position: "absolute", bottom: 14, left: 16, display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.6)", borderRadius: 99, padding: "4px 10px", backdropFilter: "blur(6px)" }}>
              <Icon name="star" size={12} color={C.gold} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{displayRating}</span>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>({reviewCount} reviews)</span>
            </div>
          </div>
        </div>

        <div style={{ padding: "22px 24px 28px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
            <div>
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 900, color: C.text, letterSpacing: -.3 }}>{coach.name}</h2>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>{coach.specialty}</div>
            </div>
            <div style={{ background: C.accentBg, border: `1px solid ${C.accentBorder}`, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 900, color: C.accent }}>€50</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5 }}>per 30 min</div>
            </div>
          </div>

          {/* Age group tags */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {coach.ageGroups.map(g => (
              <span key={g} style={{ background: ageBg[g] || C.accentBg, color: ageColor[g] || C.accent, border: `1px solid ${ageColor[g] || C.accent}33`, borderRadius: 999, padding: "3px 10px", fontSize: 10, fontWeight: 700 }}>{g}</span>
            ))}
            {coach.languages.length > 1 && coach.languages.map(l => (
              <span key={l} style={{ background: C.bgElevated, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: 999, padding: "3px 10px", fontSize: 10, fontWeight: 600 }}>{l}</span>
            ))}
          </div>

          {/* Bio */}
          <div className="accent-line" />
          <p style={{ fontSize: 13, color: C.textSec, lineHeight: 1.72, marginBottom: 16 }}>{coach.bio}</p>

          {/* Qualifications */}
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 8 }}>Qualifications</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 22 }}>
            {coach.qualifications.map(q => (
              <span key={q} className="badge badge-accent" style={{ fontSize: 10 }}>{q}</span>
            ))}
          </div>

          {/* CTA */}
          <a href={coach.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: 14, fontSize: 15, borderRadius: 12, marginBottom: 10 }}>
            Book a Lesson with {coach.name.split(" ")[0]} →
          </a>
          {coach.contactEnabled && coach.contactWhatsapp && (
            <a
              href={`https://wa.me/${coach.contactWhatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ width: "100%", justifyContent: "center", padding: 14, fontSize: 15, borderRadius: 12, background: "#25D366", marginBottom: 10 }}
            >
              <Icon name="whatsapp" size={16} color="#fff" style={{ marginRight: 8 }} />
              {coach.contactLabel || "Contact via WhatsApp"}
            </a>
          )}
          <button onClick={onClose} className="btn-ghost"
            style={{ width: "100%", justifyContent: "center" }}>
            Close
          </button>

          {/* Reviews Section */}
          <div className="accent-line" style={{ margin: "24px 0 16px" }} />
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .6, marginBottom: 12 }}>
            Reviews ({reviewCount})
          </div>
          {reviews.length === 0 ? (
            <div style={{ fontSize: 13, color: C.textMuted, fontStyle: "italic" }}>
              No reviews yet. Be the first to leave a review!
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {reviews.map((review, idx) => (
                <div key={review.id || idx} style={{ background: C.bgElevated, borderRadius: 12, padding: 14, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.accentBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.accent }}>
                          {(review.reviewer_name || "Anonymous").charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{review.reviewer_name || "Anonymous"}</div>
                        <div style={{ fontSize: 10, color: C.textMuted }}>
                          {new Date(review.created_at).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <Icon name="star" size={11} color={C.gold} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{review.rating}.0</span>
                    </div>
                  </div>
                  {review.comment && (
                    <p style={{ fontSize: 12, color: C.textSec, lineHeight: 1.5, margin: 0 }}>
                      {review.comment}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   COACH CARD
───────────────────────────────────────── */
const CoachCard = ({ coach, onView, onRate, C, delay = 0, ratingStats }) => {
  const ageColor = { Children: C.accent, Adults: C.success, Adaptive: C.gold };
  const stats = ratingStats || { averageRating: 0, reviewCount: 0 };
  const displayRating = stats.averageRating > 0 ? stats.averageRating.toFixed(1) : "—";
  const reviewCount = stats.reviewCount || 0;

  return (
    <div
      className="coach-card card-lift"
      onClick={() => onView(coach)}
      style={{ animationDelay: `${delay}s`, animation: `fadeUp .55s ${delay}s cubic-bezier(.16,1,.3,1) both`, cursor: "pointer" }}
    >
      {/* Image */}
      <div className="coach-img-wrap">
        <img src={coach.img} alt={coach.name} className="coach-img"
          onError={e => { e.target.style.display = "none"; e.target.parentNode.style.background = C.bgElevated; }} />
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${C.mode === "dark" ? "rgba(10,10,10,0.55)" : "rgba(10,29,68,0.35)"} 0%, transparent 55%)` }} />
        {/* Age badges top-left */}
        <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {coach.ageGroups.map(g => (
            <span key={g} style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", color: ageColor[g] || C.accent, border: `1px solid ${ageColor[g] || C.accent}55`, borderRadius: 999, padding: "2px 8px", fontSize: 9, fontWeight: 700 }}>{g}</span>
          ))}
        </div>
        {/* Rating top-right */}
        <button
          onClick={(e) => { e.stopPropagation(); onView(coach); }}
          style={{ position: "absolute", top: 10, right: 10, display: "flex", alignItems: "center", gap: 3, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", borderRadius: 8, padding: "4px 8px", border: "none", cursor: "pointer" }}
        >
          <Icon name="star" size={11} color={C.gold} />
          <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>{displayRating}</span>
          {reviewCount > 0 && <span style={{ fontSize: 9, color: "rgba(255,255,255,0.6)" }}>({reviewCount})</span>}
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: "16px 16px 18px" }}>
        <div style={{ marginBottom: 2 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 17, fontWeight: 800, color: C.text, letterSpacing: -.2 }}>{coach.name}</div>
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{coach.specialty}</div>
        </div>

        {/* Qualifications chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, margin: "10px 0 12px" }}>
          {coach.qualifications.slice(0, 2).map(q => (
            <span key={q} className="badge badge-accent" style={{ fontSize: 9 }}>{q}</span>
          ))}
          {coach.qualifications.length > 2 && (
            <span className="badge badge-accent" style={{ fontSize: 9 }}>+{coach.qualifications.length - 2} more</span>
          )}
        </div>

        {/* Languages */}
        {coach.languages.length > 1 && (
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
            <span style={{ fontWeight: 700, color: C.textSec }}>Speaks: </span>{coach.languages.join(" · ")}
          </div>
        )}

        {/* Price + CTA row */}
        <div style={{ display: "flex", gap: 8, alignItems: "stretch", marginBottom: 8 }}>
          <a href={coach.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-primary"
            style={{ flex: 1, justifyContent: "center", fontSize: 12, padding: "10px 12px" }}>
            Book Now
          </a>
          <button onClick={() => onView(coach)} className="btn-ghost"
            style={{ padding: "10px 14px", fontSize: 12 }}>
            Profile
          </button>
        </div>

        {/* WhatsApp Contact */}
        {coach.contactEnabled && coach.contactWhatsapp && (
          <a
            href={`https://wa.me/${coach.contactWhatsapp.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "8px 12px", fontSize: 12, background: "#25D366", marginBottom: 8 }}
          >
            <Icon name="whatsapp" size={12} color="#fff" style={{ marginRight: 6 }} />
            {coach.contactLabel || "Contact via WhatsApp"}
          </a>
        )}

        {/* Rate button */}
        <button onClick={() => onRate(coach)} className="btn-ghost"
          style={{ width: "100%", justifyContent: "center", fontSize: 11, padding: "8px 14px" }}>
          <Icon name="star" size={10} color={C.gold} />
          Leave a review
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   RATINGS MODAL
───────────────────────────────────────── */
const RatingsModal = ({ coach, onClose, onSubmit, C, submitting }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (rating === 0) return;
    if (!isAnonymous && !reviewerName.trim()) {
      toast.error('Please enter your name or choose to post anonymously');
      return;
    }
    onSubmit(coach.id, rating, comment, isAnonymous ? "" : reviewerName.trim() || "Anonymous");
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-sheet" style={{
        maxWidth: 480,
        maxHeight: "85vh",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch"
      }}>
        <div style={{ padding: "20px 16px" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 20, fontWeight: 900, color: C.text, letterSpacing: -.3 }}>
                Review {coach.name}
              </h2>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Share your experience</div>
            </div>
            <button aria-label="Close dialog" onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", background: C.bgElevated, border: `1px solid ${C.border}`, color: C.textSec, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="close" size={16} color={C.textSec} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Star Rating */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 10 }}>Your Rating</div>
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    aria-label={`${star} ${star === 1 ? "star" : "stars"}`}
                    aria-pressed={rating === star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      transition: "transform 0.15s"
                    }}
                  >
                    <svg width={32} height={32} viewBox="0 0 24 24" fill={star <= (hoverRating || rating) ? C.gold : C.bgElevated}>
                      <path d={PATHS.star} />
                    </svg>
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <div style={{ textAlign: "center", fontSize: 12, color: C.textSec, marginTop: 6 }}>
                  {["Poor", "Fair", "Good", "Very Good", "Excellent"][rating - 1]}
                </div>
              )}
            </div>

            {/* Name Input */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>
                Your Name
              </div>
              <input
                type="text"
                value={reviewerName}
                onChange={e => setReviewerName(e.target.value)}
                placeholder="Enter your name"
                disabled={isAnonymous}
                style={{
                  width: "100%",
                  background: isAnonymous ? C.bgElevated : C.inputBg,
                  border: `1.5px solid ${isAnonymous ? C.border : C.inputBorder}`,
                  borderRadius: 8,
                  color: isAnonymous ? C.textMuted : C.text,
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: 10,
                  outline: "none",
                  transition: "border-color 0.15s"
                }}
                onFocus={e => { if (!isAnonymous) e.target.style.borderColor = C.accent; }}
                onBlur={e => { if (!isAnonymous) e.target.style.borderColor = C.inputBorder; }}
              />
            </div>

            {/* Anonymous Toggle */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={e => setIsAnonymous(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: C.accent, cursor: "pointer" }}
                />
                <span style={{ fontSize: 13, color: C.textSec }}>Post anonymously</span>
              </label>
            </div>

            {/* Comment */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>
                Review (optional)
              </div>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Tell others about your experience..."
                rows={3}
                style={{
                  width: "100%",
                  background: C.inputBg,
                  border: `1.5px solid ${C.inputBorder}`,
                  borderRadius: 8,
                  color: C.text,
                  fontFamily: "'DM Sans',sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  padding: 10,
                  outline: "none",
                  resize: "none",
                  transition: "border-color 0.15s"
                }}
                onFocus={e => e.target.style.borderColor = C.accent}
                onBlur={e => e.target.style.borderColor = C.inputBorder}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={rating === 0 || submitting}
              className="btn-primary"
              style={{
                width: "100%",
                justifyContent: "center",
                padding: 12,
                fontSize: 14,
                fontWeight: 700,
                opacity: rating === 0 || submitting ? 0.5 : 1,
                cursor: rating === 0 || submitting ? "not-allowed" : "pointer",
                marginTop: 8
              }}
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function NACLessons() {
  const navigate = useNavigate();
  const [C, toggleTheme, isDark] = useTheme();
  const [activeAge, setActiveAge] = useState("All");
  const [search, setSearch]       = useState("");
  const [modal, setModal]         = useState(null);
  const [ratingsModal, setRatingsModal] = useState(null);
  const [coaches, setCoaches]     = useState([]);
  const [coachRatings, setCoachRatings] = useState({});
  const [coachReviews, setCoachReviews] = useState({});
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(true);

  // Fetch coaches from Supabase
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const { data, error } = await supabase
          .from('sport_ireland_swim_coaches')
          .select('*')
          .order('name');

        if (error) throw error;

        setCoaches(data.map(c => ({
          id: c.id,
          name: c.name,
          img: c.img,
          specialty: c.specialty,
          bio: c.bio,
          qualifications: c.qualifications || [],
          languages: c.languages || [],
          ageGroups: c.age_groups || [],
          rating: c.rating,
          reviews: c.reviews,
          bookingUrl: c.booking_url,
          contactEnabled: c.contact_enabled,
          contactWhatsapp: c.contact_whatsapp,
          contactLabel: c.contact_label,
          contactType: c.contact_type,
        })).sort((a, b) => {
          // Put Nikita first, then sort alphabetically
          if (a.name.toLowerCase().includes('nikita')) return -1;
          if (b.name.toLowerCase().includes('nikita')) return 1;
          return a.name.localeCompare(b.name);
        }));
      } catch (err) {
        console.error('Error fetching coaches:', err);
      }
    };

    fetchCoaches();
  }, []);

  // Fetch ratings for all coaches on mount
  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const { data, error } = await supabase
          .from('sport_ireland_swim_coach_ratings')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        // Calculate average rating and count per coach
        const ratingsMap = {};
        const reviewsMap = {};

        data.forEach(r => {
          if (!ratingsMap[r.coach_id]) {
            ratingsMap[r.coach_id] = { sum: 0, count: 0 };
            reviewsMap[r.coach_id] = [];
          }
          ratingsMap[r.coach_id].sum += r.rating;
          ratingsMap[r.coach_id].count += 1;
          reviewsMap[r.coach_id].push(r);
        });

        const result = {};
        Object.keys(ratingsMap).forEach(coachId => {
          result[coachId] = {
            averageRating: ratingsMap[coachId].sum / ratingsMap[coachId].count,
            reviewCount: ratingsMap[coachId].count
          };
        });

        setCoachRatings(result);
        setCoachReviews(reviewsMap);
      } catch (err) {
        console.error('Error fetching ratings:', err);
      } finally {
        setRatingsLoading(false);
      }
    };

    fetchRatings();
  }, []);

  useEffect(() => { injectCSS(C); }, [C]);

  const filtered = coaches.filter(c => {
    const matchAge = activeAge === "All" || c.ageGroups.includes(activeAge);
    const q = search.toLowerCase();
    const matchSearch = !q ||
      c.name.toLowerCase().includes(q) ||
      c.specialty.toLowerCase().includes(q) ||
      c.qualifications.some(x => x.toLowerCase().includes(q)) ||
      c.languages.some(l => l.toLowerCase().includes(q));
    return matchAge && matchSearch;
  });

  const BOOK_ALL = "https://sportirelandcampus.courseprogress.co.uk/onlinejoining/?redirect=%2Fclasses-results%3Ffilter%3D%7B%2522showFullCourses%2522:false,%2522centre%2522:1,%2522courseGroupCategory%2522:%5B28%5D%7D";

  // Handle rating submission
  const handleSubmitRating = async (coachId, rating, comment, reviewerName) => {
    setSubmittingRating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Check if user has already rated this coach (only for authenticated users)
      let existing = null;
      if (user) {
        const { data: existingData, error: existingError } = await supabase
          .from('sport_ireland_swim_coach_ratings')
          .select('id')
          .eq('coach_id', coachId)
          .eq('user_id', user.id)
          .single();

        if (existingError && existingError.code !== 'PGRST116') {
          throw existingError;
        }
        existing = existingData;
      }

      if (existing) {
        // Update existing rating
        const { error } = await supabase
          .from('sport_ireland_swim_coach_ratings')
          .update({ rating, comment, reviewer_name: reviewerName, updated_at: new Date().toISOString() })
          .eq('id', existing.id);
        if (error) throw error;
        toast.success('Review updated!');
      } else {
        // Insert new rating (anonymous or authenticated)
        const { error } = await supabase
          .from('sport_ireland_swim_coach_ratings')
          .insert({
            coach_id: coachId,
            user_id: user?.id || null,
            rating,
            comment,
            reviewer_name: reviewerName
          });
        if (error) throw error;
        toast.success('Review submitted!');
      }

      // Refresh ratings
      const { data: updatedRatings, error: ratingsError } = await supabase
        .from('sport_ireland_swim_coach_ratings')
        .select('coach_id, rating');

      if (ratingsError) throw ratingsError;

      const ratingsMap = {};
      updatedRatings.forEach(r => {
        if (!ratingsMap[r.coach_id]) {
          ratingsMap[r.coach_id] = { sum: 0, count: 0 };
        }
        ratingsMap[r.coach_id].sum += r.rating;
        ratingsMap[r.coach_id].count += 1;
      });

      // Refresh ratings and reviews
      const { data: allData, error: allError } = await supabase
        .from('sport_ireland_swim_coach_ratings')
        .select('*')
        .order('created_at', { ascending: false });

      if (allError) throw allError;

      const newRatingsMap = {};
      const newReviewsMap = {};

      allData.forEach(r => {
        if (!newRatingsMap[r.coach_id]) {
          newRatingsMap[r.coach_id] = { sum: 0, count: 0 };
          newReviewsMap[r.coach_id] = [];
        }
        newRatingsMap[r.coach_id].sum += r.rating;
        newRatingsMap[r.coach_id].count += 1;
        newReviewsMap[r.coach_id].push(r);
      });

      const newResult = {};
      Object.keys(newRatingsMap).forEach(coachId => {
        newResult[coachId] = {
          averageRating: newRatingsMap[coachId].sum / newRatingsMap[coachId].count,
          reviewCount: newRatingsMap[coachId].count
        };
      });

      setCoachRatings(newResult);
      setCoachReviews(newReviewsMap);
      setRatingsModal(null);
    } catch (err) {
      console.error('Error submitting rating:', err);
    } finally {
      setSubmittingRating(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>

      {/* ── HEADER ── same structure as ProAmInsights */}
      <header style={{ position: "sticky", top: 0, zIndex: 100, height: 56, display: "flex", alignItems: "center", gap: 12, padding: "0 16px", background: C.headerBg, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", borderBottom: `1px solid ${C.border}` }}>
        <button onClick={() => navigate("/")} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0, minWidth: 0, flex: 1, overflow: "hidden" }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: C.bgElevated, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <img src={logo} alt="Snows ProAm" style={{ width: 18, height: 18, objectFit: "contain" }} />
          </div>
          <div className="header-title" style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Sport Ireland <span style={{ color: C.accent }}>Swimming</span>
          </div>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <button className="theme-btn" onClick={toggleTheme} title={isDark ? "Light mode" : "Dark mode"}>
            <Icon name={isDark ? "sun" : "moon"} size={14} color={C.textSec} />
          </button>
          <a href={BOOK_ALL} target="_blank" rel="noopener noreferrer" className="btn-primary header-btn" style={{ padding: "8px 12px", fontSize: 12, whiteSpace: "nowrap" }}>
            Book
          </a>
        </div>
      </header>

      {/* ── HERO ── same gradient approach as PartnerPortal public profile */}
      <div style={{ position: "relative", background: `linear-gradient(135deg, ${C.accentDark} 0%, ${C.accent} 100%)`, overflow: "hidden", padding: "56px 24px 0" }}>
        {/* Ambient glow blobs */}
        <div style={{ position: "absolute", top: "-40%", right: "-10%", width: "55vw", height: "55vw", background: "radial-gradient(ellipse, rgba(91,168,247,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-30%", left: "-5%", width: "40vw", height: "40vw", background: "radial-gradient(ellipse, rgba(12,105,200,0.4) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 780, margin: "0 auto", position: "relative", textAlign: "center" }}>
          {/* Verified badge — same style as PartnerPortal */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 999, padding: "5px 14px", marginBottom: 22, backdropFilter: "blur(8px)" }}>
            <Icon name="trophy" size={12} color="rgba(255,255,255,0.8)" />
            <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: .6 }}>Sport Ireland National Aquatic Centre</span>
          </div>

          <h1 className="display-head fu" style={{ color: "#fff", marginBottom: 16, fontSize: "clamp(28px,5vw,52px)" }}>
           
Learn from Ireland's best
swim coaches
          </h1>
          <p className="fu1" style={{ fontSize: "clamp(13px,2vw,16px)", color: "rgba(255,255,255,0.72)", maxWidth: 560, margin: "0 auto 28px", lineHeight: 1.65 }}>
            One-to-one lessons with world-class coaches in an Olympic-standard pool. Choose the instructor that's right for you, children aged 3+, adults, and adaptive lessons available.
          </p>

          {/* Price pill */}
          <div className="fu2" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, flexWrap: "wrap", marginBottom: 40 }}>
            {[
              { icon: "trophy", label: "€50 per session" },
              { icon: "calendar", label: "30 minutes, 1-to-1" },
              { icon: "location", label: "NAC, Blanchardstown" },
            ].map(item => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 7, background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 999, padding: "7px 16px", backdropFilter: "blur(6px)" }}>
                <Icon name={item.icon} size={12} color="rgba(255,255,255,0.7)" />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Wave divider — same as PartnerPortal structure */}
        <div style={{ lineHeight: 0, marginTop: -2 }}>
          <svg viewBox="0 0 1440 48" preserveAspectRatio="none" style={{ display: "block", width: "100%", height: 48 }}>
            <path d="M0,32 C360,0 1080,56 1440,20 L1440,48 L0,48 Z" fill={C.bg} />
          </svg>
        </div>
      </div>

      {/* ── INFO STRIP ── same card style as PartnerPortal */}
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "28px 20px 0" }}>
        <div className="info-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 32 }}>
          {[
            { icon: "swim", title: "Olympic-standard pool", sub: "10-lane 50m × 25m, 2 moveable floors" },
            { icon: "calendar", title: "Flexible scheduling", sub: "Select your coach, view availability online" },
            { icon: "phone", title: "48-hr cancellation policy", sub: "Call 01-6464300 or email sales@sportirelandcampus.ie" },
            { icon: "globe", title: "Children 3+ · All adults", sub: "Lessons booked at 1.2m depth" },
          ].map(item => (
            <div key={item.title} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 14, padding: "16px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: C.accentBg, border: `1px solid ${C.accentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={item.icon} size={16} color={C.accent} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 3 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.5 }}>{item.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── FILTERS — same tab-pill pattern as PartnerPortal ── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
          <div>
            <div className="accent-line" />
            <h2 className="section-head" style={{ marginBottom: 0 }}>Choose Your Coach</h2>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* Search */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                <Icon name="search" size={14} color={C.textMuted} />
              </span>
              <input
                type="text"
                className="search-input"
                placeholder="Name, language, skill…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: "100%", minWidth: 150, flex: "0 1 200px" }}
              />
            </div>
            {/* Age filters */}
            {AGE_FILTERS.map(f => (
              <button key={f} className={`tab-pill${activeAge === f ? " active" : ""}`} onClick={() => setActiveAge(f)}>{f}</button>
            ))}
          </div>
        </div>
        <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 20 }}>
          Showing <strong style={{ color: C.textSec }}>{filtered.length}</strong> of {coaches.length} coaches
          {activeAge !== "All" && <> · <span style={{ color: C.accent }}>{activeAge}</span></>}
          {search && <> · "<span style={{ color: C.accent }}>{search}</span>"</>}
        </div>

        {/* ── COACHES GRID ── */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18, marginBottom: 40 }}>
            <Icon name="search" size={40} color={C.textMuted} />
            <div className="section-head" style={{ fontSize: 17, marginTop: 14, marginBottom: 6 }}>No coaches match your search</div>
            <p className="section-sub">Try broadening your filters or clearing the search.</p>
          </div>
        ) : (
          <div className="coach-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16, marginBottom: 60 }}>
            {filtered.map((c, i) => (
              <CoachCard
                key={c.id}
                coach={c}
                onView={setModal}
                onRate={setRatingsModal}
                C={C}
                delay={Math.min(i, 5) * 0.06}
                ratingStats={coachRatings[c.id]}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {modal && <CoachModal coach={modal} onClose={() => setModal(null)} C={C} ratingStats={coachRatings[modal.id]} reviews={coachReviews[modal.id] || []} />}

      {/* ── RATINGS MODAL ── */}
      {ratingsModal && (
        <RatingsModal
          coach={ratingsModal}
          onClose={() => setRatingsModal(null)}
          onSubmit={handleSubmitRating}
          C={C}
          submitting={submittingRating}
        />
      )}

      {/* ── FOOTER ── identical to PartnerPortal */}
      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "18px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: C.textMuted, fontFamily: "'Sora',sans-serif", textTransform: "uppercase", letterSpacing: .5 }}>
          Powered by <span className="grad-text" style={{ fontWeight: 700 }}>Snows ProAm</span>
          {" · "}
          <a href="https://www.sportirelandcampus.ie" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600, textDecoration: "none" }}>Sport Ireland Campus</a>
          {" · "}
          <a href="mailto:sales@sportirelandcampus.ie" style={{ color: C.accent, fontWeight: 600, textDecoration: "none" }}>sales@sportirelandcampus.ie</a>
          {" · "}
          <a href="https://www.sportirelandcampus.ie/sport-ireland-campus-sic-terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: C.accent, fontWeight: 600, textDecoration: "none" }}>Terms & Conditions</a>
        </div>
      </footer>
    </div>
  );
}